/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import ImgixWithPrioritizedLoading from 'js/components/ImgixWithPrioritizedLoading';

import { Link, Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { BasicCard } from 'bundles/cds-labs/components';

const IMGPARAMS = {
  quality: 80,
};

const MAX_IMAGE_HEIGHT = 300;

const styles = {
  link: css`
    text-decoration: none;
    width: 100%;
    height: 100%;
    flex-grow: 1;

    &:focus,
    &:active {
      text-decoration: none;
    }
  `,
  card: css`
    width: 100%;

    ${breakpoints.up('sm')} {
      border: none;
      height: 100%;
      padding: var(--cds-spacing-100);
    }

    ${breakpoints.down('xs')} {
      display: flex;
      flex-direction: row;
      align-items: center;
    }
  `,
  titleContainer: css`
    display: inline-flex;
    align-items: center;

    ${breakpoints.down('xs')} {
      flex-grow: 1;
      justify-content: space-between;
      ${typography2.subtitleLarge};
    }
  `,
  title: css`
    margin-right: var(--cds-spacing-100);
  `,
  icon: css`
    ${breakpoints.down('xs')} {
      display: flex;
      align-items: center;
      margin-right: var(--cds-spacing-200);
    }
  `,
  imageContainer: css`
    height: 180px;
    overflow: hidden;
    border-radius: var(--cds-border-radius-100);
    margin-bottom: var(--cds-spacing-150);

    ${breakpoints.down('xs')} {
      height: 54px;
      width: 56px;
      margin: var(--cds-spacing-150);
      flex-shrink: 0;
    }
  `,
  image: css`
    width: 100%;
    height: 100%;
    object-fit: cover;
  `,
  body: css`
    margin: var(--cds-spacing-100) var(--cds-spacing-300) 0 0;

    ${breakpoints.down('xs')} {
      display: none;
    }
  `,
};

export type BucketCardProps = {
  imageUrl: string;
  title: string;
  body: string;
  url: string;
  linkName: string;
  index: number;
};

const BucketCard = ({ imageUrl, title, body, url, linkName, index }: BucketCardProps) => {
  const track = useTracker();
  const visibilityTrackerRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_bucket_card', {
    bucketCardIndex: index,
    bucketCardName: linkName,
    bucketCardLink: url,
  });

  return (
    <Link
      href={url}
      css={styles.link}
      onClick={() => {
        track('click_button', {
          button: {
            name: 'lohp_bucket',
            linkName,
            linkURL: url,
            linkType: 'internal',
          },
        });
      }}
    >
      <BasicCard css={styles.card} ref={visibilityTrackerRef}>
        <div css={styles.imageContainer}>
          <ImgixWithPrioritizedLoading
            src={imageUrl}
            alt=""
            imgParams={IMGPARAMS}
            maxHeight={MAX_IMAGE_HEIGHT}
            css={styles.image}
          />
        </div>
        <Typography2 component="h2" variant="titleSmall" css={styles.titleContainer}>
          <span css={styles.title}>{title}</span>
          <span css={styles.icon}>
            <ArrowNextIcon size="medium" color="default" />
          </span>
        </Typography2>
        <Typography2 component="p" variant="bodyPrimary" color="supportText" css={styles.body}>
          {body}
        </Typography2>
      </BasicCard>
    </Link>
  );
};

export default BucketCard;
